import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Car, Columns2, CheckCircle2, MessageCircle, Send, Copy, Share2, BadgePercent, BadgeDollarSign, HandCoins, Gift, CalendarClock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CurrencyInput } from '../components/shared/CurrencyInput';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { vehiclePublicAPI } from '../lib/api';
import { compareStore, convStore } from '../lib/compareStore';
import VehicleShowroom from '../components/showroom/VehicleShowroom';
import { attachTactile, feedback, setOrgTactile } from '../components/showroom/tactile';
import { BuyerChatThread } from '../components/showroom/BuyerChatThread';
import { ActionRow } from '../components/showroom/ActionChips';
import { buyerSessionId, buyerVisitId, useSessionContact, mergeKnown } from '../components/showroom/useFormDraft';
import { visitStore } from '../lib/visitStore';
import { fmtCurrency, fmtNumber } from '../lib/financeMath';
import MyVisitTray from '../components/showroom/MyVisitTray';
import LanguageGate, { useLanguageGate, ChangeLanguageLink, ENABLED_LANGUAGES_FALLBACK } from '../components/shared/LanguageGate';
import { resolveBuyerTheme } from '../components/showroom/buyerTheme';
import { WelcomeInterstitial, wasWelcomeSeen, markWelcomeSeen } from '../components/showroom/WelcomeInterstitial';

const emailOk = (e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e || '');

// Bumped whenever the consent WORDING changes, so an old audit row still
// proves exactly what that buyer saw. Mirrors services/buyer_consent.py.
const CONSENT_VERSION = 'vehicle-2026-09-v1';

export default function VehicleExperience() {
    const { cardId } = useParams();
    const navigate = useNavigate();
    const [welcomeDone, setWelcomeDone] = useState(false);
    // Ticket number handed back by Save, shown on the confirmation.
    const [ticket, setTicket] = useState('');
    const { t, i18n } = useTranslation();
    const [data, setData] = useState(null);
    const [specialOpen, setSpecialOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [action, setAction] = useState(null);          // 'contact' | 'testdrive' | 'save'
    // 9B — which door the buyer came through: 'book' or 'gift'. Both book the
    // SAME drive (the gift only exists once the drive is completed), but the
    // desk gets to know what brought them in.
    const [tdIntent, setTdIntent] = useState(null);
    // One identity per visit — every sheet on this page prefills from it.
    const sessionId = cardId ? buyerSessionId(cardId) : null;
    // Per TAB, not per card — this is what links the three vehicles a shopper
    // scans on one walk around the lot. The per-vehicle sessionId above is
    // unchanged and still owns financing/warranty/insurance.
    const visitId = cardId ? buyerVisitId() : null;
    const [visitCount, setVisitCount] = useState(0);
    const known = useSessionContact(cardId, sessionId);
    const [f, setF] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [cmpCount, setCmpCount] = useState(0);
    const [inCompare, setInCompare] = useState(false);
    const [introOpen, setIntroOpen] = useState(false);
    const [chat, setChat] = useState(null);              // {reqId|null, messages, salesStatus} | null
    const [chatInput, setChatInput] = useState('');
    const [chatLead, setChatLead] = useState({});        // {name, contact} — first-time inline capture
    const [od, setOd] = useState(null);                  // 'choose' | 'offer' | 'deposit'
    const [odForm, setOdForm] = useState({});
    const [odBusy, setOdBusy] = useState(false);
    const [odDone, setOdDone] = useState(null);          // {kind, request_id, etransfer_email}
    const [counterBusy, setCounterBusy] = useState(false);
    const [share, setShare] = useState(false);
    const [shareForm, setShareForm] = useState({});
    const [shareBusy, setShareBusy] = useState(false);
    const [shareSent, setShareSent] = useState(false);
    const [shareUrl, setShareUrl] = useState('');
    const pollRef = useRef(null);

    const orgId = data?.organization_id;
    const upd = (k, val) => setF((p) => ({ ...p, [k]: val }));

    // EXISTING shared language contract (Card Settings enabled_languages → org
    // → default), resolved server-side and handed to the SHARED LanguageGate:
    // one language → picker skipped and applied; more than one → picker first,
    // choice remembered per card, stale choice re-prompts.
    const enabledLangs = data?.found === undefined
        ? null
        : (Array.isArray(data?.enabled_languages) && data.enabled_languages.length
            ? data.enabled_languages
            : ENABLED_LANGUAGES_FALLBACK);
    const { needsPicker, choose, reset: resetLang } = useLanguageGate(enabledLangs, `veh:${cardId}`);

    useEffect(() => {
        vehiclePublicAPI.get(cardId, sessionId, visitId)
            .then((r) => {
                setOrgTactile(r.data?.action_noise);
                setData(r.data);
                if (wasWelcomeSeen(r.data?.organization_id)) setWelcomeDone(true);
                const oid = r.data?.organization_id;
                if (oid) {
                    compareStore.setActiveOrg(oid);
                    setCmpCount(compareStore.count(oid));
                    setInCompare(compareStore.has(oid, r.data?.card_id || cardId));
                }
                // Passive breadcrumb: opening a card puts it in the visit tray
                // with no decision asked of the shopper. NOT the comparison
                // list — that stays an explicit action.
                if (oid && r.data?.vehicle) {
                    const v = r.data.vehicle;
                    setVisitCount(visitStore.add(oid, {
                        card_id: r.data?.card_id || cardId,
                        label: [v.year, v.make, v.model].filter(Boolean).join(' ') || null,
                        photo: (v.photos || [])[0] || null,
                        price: v.price ?? null,
                    }).length);
                }
                if (r.data?.vehicle?.special_offer_enabled && r.data?.vehicle?.special_offer_url) {
                    vehiclePublicAPI.offerEvent(cardId, 'view', sessionId).catch(() => {});
                }
            })
            .catch(() => setData({ found: false, status: 'error' }))
            .finally(() => setLoading(false));
    }, [cardId, sessionId, visitId]);

    // Shared-link attribution: /vehicle/{card}?ref={share_token}. Fire ONCE,
    // record the open/view via the share endpoint, then strip the param so a
    // refresh never re-counts. Reuses the existing activity_events pipeline.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const ref = params.get('ref');
        if (!ref) return;
        vehiclePublicAPI.openShare(ref).catch(() => {});
        params.delete('ref');
        const qs = params.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    }, [cardId]);

    // Buyer deep-link resume: /vehicle/{card}?c={request_id} hydrates the
    // EXISTING convStore so a fresh tab/window/profile can resume a known
    // conversation. Reuses the existing request_id — no new identity system.
    // The param is stripped from the visible URL after hydration.
    useEffect(() => {
        const sp = new URLSearchParams(window.location.search);
        const c = sp.get('c');
        if (c && cardId) {
            try { convStore.set(cardId, c); } catch (_e) { /* ignore */ }
            sp.delete('c');
            const qs = sp.toString();
            window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
        }
    }, [cardId]);

    // Part 8 — ONE delegated tactile listener at document level, so the
    // portalled sheets (Radix renders them outside this tree) are covered too.
    useEffect(() => attachTactile(document.body), []);

    // PHASE F — a manager authorizes the Promotion in Command Center while the
    // buyer is ALREADY on this page, so the hero badge has to appear with no
    // refresh. Poll only the promotion slice (5s, the request-progress cadence),
    // skip while the tab is hidden, and pull immediately when it becomes visible
    // so switching back to the buyer tab shows the badge already pulsing.
    //
    // GO LIVE rides along on this SAME tick: one "still here" heartbeat so the
    // sales desk can tell this buyer is on the page right now. No new timer, no
    // new cadence, and the heartbeat is deliberately a separate endpoint —
    // /promo stays side-effect-free, which is the whole reason it exists.
    useEffect(() => {
        if (!data?.found) return;
        let alive = true;
        const pull = () => {
            vehiclePublicAPI.promo(cardId)
                .then((r) => {
                    if (!alive) return;
                    setData((d) => (d ? { ...d, eligible_special: r.data?.eligible_special || null } : d));
                })
                .catch(() => {});
            if (sessionId) vehiclePublicAPI.presence(cardId, sessionId, visitId).catch(() => {});
        };
        pull();
        const iv = setInterval(() => { if (!document.hidden) pull(); }, 5000);
        const onVis = () => { if (!document.hidden) pull(); };
        document.addEventListener('visibilitychange', onVis);
        return () => {
            alive = false;
            clearInterval(iv);
            document.removeEventListener('visibilitychange', onVis);
        };
    }, [cardId, data?.found, sessionId, visitId]);

    // Poll the sales conversation while the chat is open. A thread the server
    // no longer has (demo reset, purged record) is FORGOTTEN rather than polled
    // forever — otherwise every later message is posted into a 404 and lost.
    useEffect(() => {
        if (!chat?.reqId) { if (pollRef.current) clearInterval(pollRef.current); return; }
        const load = () => vehiclePublicAPI.getConversation(chat.reqId)
            .then((r) => setChat((c) => c ? { ...c, messages: r.data.messages || [], salesStatus: r.data.sales_status, negotiation: r.data.negotiation || null } : c))
            .catch((e) => {
                if (e?.response?.status !== 404) return;
                convStore.clear(data?.card_id || cardId);
                setChat((c) => (c ? { ...c, reqId: null, messages: [] } : c));
            });
        load();
        pollRef.current = setInterval(load, 8000);
        return () => pollRef.current && clearInterval(pollRef.current);
    }, [chat?.reqId]);

    // ?save=1 — the "Save and finish later" jump from the F&I / trade-in screens.
    // Runs only once the buyer page itself is on screen — never behind the
    // language gate or the welcome interstitial, and the param is stripped so a
    // refresh or a back-navigation does not re-open the sheet.
    useEffect(() => {
        if (!data?.found || needsPicker) return;
        if (data.welcome_screen && !welcomeDone) return;
        const sp = new URLSearchParams(window.location.search);
        // ?chat=1 — coming back from the emailed ticket link: open the live thread.
        if (sp.get('chat') === '1') {
            setChat((c) => c || { reqId: convStore.get(myCard) || null, messages: [], salesStatus: data.sales_status });
            sp.delete('chat');
            const q0 = sp.toString();
            window.history.replaceState({}, '', `${window.location.pathname}${q0 ? `?${q0}` : ''}`);
            return;
        }
        if (sp.get('save') !== '1') return;
        setAction('save'); setF({}); setDone(false); setTdIntent(null);
        sp.delete('save');
        const qs = sp.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    }, [data, needsPicker, welcomeDone]);

    if (loading) {
        return <div className="min-h-screen bg-neutral-950 flex items-center justify-center text-neutral-400" data-testid="ve-loading">{t('common.loading', 'Loading…')}</div>;
    }

    const notAvailable = !data?.found || ['unassigned', 'sold', 'unknown_tag', 'error'].includes(data.status) || !data?.vehicle;

    // Language FIRST — before any vehicle content, matching every other
    // public YulOnTap surface.
    if (needsPicker && enabledLangs) {
        return (
            <div data-testid="ve-language-gate">
                <LanguageGate
                    enabled={enabledLangs}
                    onSelect={choose}
                    variant="dark"
                    orgName={data?.dealership}
                    wallpaperUrl={data?.vehicle?.wallpaper_url || null}
                />
            </div>
        );
    }

    if (notAvailable) {
        return (
            <div className="min-h-screen bg-neutral-950 text-neutral-100 flex flex-col items-center justify-center px-6 text-center gap-4" data-testid="ve-unavailable">
                <Car className="w-14 h-14 text-neutral-400" />
                <h1 className="text-xl font-semibold text-neutral-100">
                    {data?.status === 'sold' ? t('vehicleExp.sold', 'This vehicle has been sold') : t('vehicleExp.unavailable', 'This vehicle is not available right now')}
                </h1>
                <p className="text-neutral-400 text-sm max-w-xs">{t('vehicleExp.unavailableHint', 'Please ask a member of our sales team for the latest inventory.')}</p>
                {data?.dealership && <p className="text-neutral-400 text-xs mt-2">{data.dealership}</p>}
            </div>
        );
    }

    const th = resolveBuyerTheme(data.buyer_theme);
    // 9A: once per visitor per DEALERSHIP, not per vehicle.
    if (data.welcome_screen && !welcomeDone) {
        return <WelcomeInterstitial dealership={data.dealership} th={th}
            dealerLogo={data.dealer_logo} copy={data.welcome_copy}
            onEnter={() => { markWelcomeSeen(data.organization_id); setWelcomeDone(true); }} />;
    }
    const v = data.vehicle;
    const title = `${v.year || ''} ${v.make || ''} ${v.model || ''}`.trim();
    const myCard = data.card_id || cardId;
    // PHASE F — customer-facing Eligible Special (authorized incentive only).
    const lang = (i18n.language || 'en').slice(0, 2);
    const special = data.eligible_special;
    const specialText = special
        ? (special.kind === 'cash'
            ? t('vehicleExp.special.cashOff', { amount: fmtNumber(special.amount || 0) })
            : (special.label?.[lang] || special.label?.en || ''))
        : '';

    const openAction = (a) => {
        setAction(a); setDone(false); setTdIntent(null);
        // The resolver hands back one full name; the test-drive sheet wants it
        // split, the other sheets want it whole.
        const parts = String(known?.name || '').trim().split(/\s+/).filter(Boolean);
        setF(mergeKnown(
            parts.length ? { first_name: parts[0], last_name: parts.slice(1).join(' ') } : {},
            known, [['name', 'name'], ['email', 'email'], ['phone', 'phone']]));
    };

    // Vehicle/financing-specific consent — NOT broad marketing consent. Same
    // wording is reused by Financing, Trade-In (Insurance has its own).
    const consentText = t('vehicleExp.consentVehicle',
        'I agree to be contacted by {{dealership}} about this vehicle and any financing or trade-in inquiries related to it.',
        { dealership: data.dealership });

    const toggleCompare = () => {
        const now = compareStore.toggle(orgId, myCard);
        setInCompare(now);
        setCmpCount(compareStore.count(orgId));
        if (now) {
            toast.success(t('vehicleExp.addedToast', 'Added to comparison'));
            if (!localStorage.getItem('yot_compare_seen')) { localStorage.setItem('yot_compare_seen', '1'); setIntroOpen(true); }
        } else {
            toast(t('vehicleExp.removedToast', 'Removed from comparison'));
        }
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            if (action === 'testdrive') {
                const hasContact = emailOk(f.email) || (f.phone || '').trim().length >= 5;
                if (!f.first_name?.trim() || !hasContact || !f.date || !f.time) { toast.error(t('vehicleExp.err.testdrive', 'First name, an email or phone, date and time are required')); setSubmitting(false); return; }
                await vehiclePublicAPI.testDrive(cardId, { first_name: f.first_name, last_name: f.last_name, email: f.email, phone: f.phone, date: f.date, time: f.time, message: f.message, gift_intent: tdIntent === 'gift',
                    session_id: sessionId });
            } else if (action === 'save') {
                // ONE action: saves the vehicle AND creates/reuses the buyer's
                // ticket. The buyer is asked for their email and nothing else.
                if (!emailOk(f.email)) {
                    toast.error(t('vehicleExp.saveTicketErr', 'A valid email is required'));
                    setSubmitting(false); return;
                }
                // Vehicle-specific consent: the EXACT wording shown is sent so the
                // dealer's audit trail proves what was agreed, not just a boolean.
                const saveRes = await vehiclePublicAPI.save(cardId, {
                    first_name: (f.first_name || '').trim() || (known?.name || 'Guest'),
                    email: f.email,
                    phone: '',
                    lang,
                    consent: !!f.vehicle_consent,
                    consent_text: consentText,
                    consent_version: CONSENT_VERSION,
                    // Part 6: the link the buyer gets carries this, so a
                    // half-filled F&I or trade-in form comes back with them.
                    session_id: sessionStorage.getItem(`yot_fi_session_${cardId}`) || null,
                });
                setTicket(saveRes.data?.ticket_code || '');
            }
            setDone(true);
            feedback('success');
        } catch (err) { feedback('error'); toast.error(err.response?.data?.detail || t('vehicleExp.err.generic', 'Something went wrong')); }
        finally { setSubmitting(false); }
    };

    const closeChat = () => { setChat(null); setChatLead({}); setChatInput(''); };

    // ── Make an Offer / Leave a Deposit ────────────────────────────────────
    const openOfferDeposit = () => { setOdForm({}); setOdDone(null); setOd('choose'); };
    const odUpd = (k, val) => setOdForm((p) => ({ ...p, [k]: val }));

    const submitOfferDeposit = async () => {
        const kind = od;                                   // 'offer' | 'deposit'
        const hasContact = emailOk(odForm.email) || (odForm.phone || '').trim().length >= 5;
        if (!hasContact) { toast.error(t('offerDeposit.errContact', 'An email or a phone number is required')); return; }
        const amount = parseFloat(String(odForm.amount || '').replace(/[^0-9.]/g, ''));
        if (!amount || amount <= 0) { toast.error(t('offerDeposit.errAmount', 'Please enter an amount')); return; }
        setOdBusy(true);
        try {
            const r = await vehiclePublicAPI.offerDeposit(cardId, {
                kind, name: odForm.name, email: odForm.email, phone: odForm.phone,
                amount, hold_until: kind === 'deposit' ? (odForm.hold_until || null) : null,
                note: odForm.note,
                // WITHOUT this the offer/deposit is recorded but belongs to no
                // visit, and Buyer Activity (which is strictly session-scoped)
                // can never show it. The money actions are the whole point of
                // the panel, so they carry the same session id as every other
                // buyer screen.
                session_id: sessionId || null,
            });
            const reqId = r.data?.request_id;
            if (reqId) convStore.set(myCard, reqId);
            setOdDone({ kind, request_id: reqId, etransfer_email: r.data?.etransfer_email || null });
        } catch (err) { toast.error(err.response?.data?.detail || t('vehicleExp.err.generic', 'Something went wrong')); }
        finally { setOdBusy(false); }
    };

    const openConversation = (reqId) => {
        setOd(null); setOdDone(null);
        setChatLead({}); setChatInput('');
        setChat({ reqId, messages: [], salesStatus: data.sales_status, negotiation: null });
    };

    const respondToCounter = async (decision) => {
        setCounterBusy(true);
        try {
            await vehiclePublicAPI.counterResponse(chat.reqId, decision);
            const r = await vehiclePublicAPI.getConversation(chat.reqId);
            setChat((c) => c ? { ...c, messages: r.data.messages || [], negotiation: r.data.negotiation || null } : c);
            toast.success(decision === 'accept'
                ? t('offerDeposit.counterAccepted', 'You accepted this counter.')
                : t('offerDeposit.counterDeclined', 'You declined this counter.'));
        } catch (err) { toast.error(err.response?.data?.detail || t('vehicleExp.err.generic', 'Something went wrong')); }
        finally { setCounterBusy(false); }
    };

    const openShareFlow = () => { setShareForm({}); setShareSent(false); setShareUrl(''); setShare(true); };

    const submitShare = async () => {
        setShareBusy(true);
        try {
            const r = await vehiclePublicAPI.share(cardId, {
                recipient_name: shareForm.name, recipient_email: shareForm.email,
                recipient_phone: shareForm.phone, message: shareForm.message,
                // The backend already accepts this and the panel already renders
                // "Sent this vehicle to someone" — it was only ever missing here.
                session_id: sessionId || null,
            });
            const token = r.data?.share_token;
            const url = `${window.location.origin}/vehicle/${cardId}?ref=${token}`;
            setShareUrl(url);
            const shareText = t('vehicleShare.messageBody', { dealership: data.dealership, vehicle: title });
            if (navigator.share) {
                try { await navigator.share({ title, text: shareText, url }); } catch { /* user cancelled — link still ready */ }
            }
            setShareSent(true);
        } catch (err) { toast.error(err.response?.data?.detail || t('vehicleExp.err.generic', 'Something went wrong')); }
        finally { setShareBusy(false); }
    };

    const copyShareLink = async () => {
        try { await navigator.clipboard.writeText(shareUrl); toast.success(t('vehicleShare.copied', 'Link copied')); }
        catch { toast.error(t('vehicleExp.err.generic', 'Something went wrong')); }
    };

    const sendChat = async () => {
        const text = chatInput.trim();
        if (!text) return;
        // Ordinary Contact Sales is CONTACT-FREE: no email/phone (or even name)
        // required to start or continue a conversation. Name defaults to "Guest";
        // optional email/phone are passed only if the customer chose to add them.
        const startThread = async () => {
            const name = (chatLead.name || '').trim() || 'Guest';
            const contact = (chatLead.contact || '').trim();
            const isEmail = contact.includes('@');
            const r = await vehiclePublicAPI.contactSales(cardId, {
                name, email: isEmail ? contact : '', phone: (contact && !isEmail) ? contact : '',
                // The buyer session for THIS visit (same id every other buyer
                // screen uses), so the desk sees one thread, not several leads.
                message: text, session_id: sessionId || null,
            });
            const reqId = r.data?.request_id;
            if (reqId) { convStore.set(myCard, reqId); setChat((c) => c ? { ...c, reqId } : c); }
        };
        if (!chat?.reqId) {
            setChatInput('');
            setChat((c) => c ? { ...c, messages: [...(c.messages || []), { sender_role: 'customer', body: text, created_at: new Date().toISOString() }] } : c);
            try { await startThread(); }
            catch { toast.error(t('vehicleExp.err.generic', 'Something went wrong')); }
            return;
        }
        setChatInput('');
        setChat((c) => ({ ...c, messages: [...(c.messages || []), { sender_role: 'customer', body: text, created_at: new Date().toISOString() }] }));
        try { await vehiclePublicAPI.postConversation(chat.reqId, text); }
        catch (e) {
            // The remembered thread is gone server-side (demo reset, purged
            // record). Forget it and OPEN A REAL ONE with the same message —
            // silently dropping it is what made a sent message reach neither
            // the Sales Desk nor the Command Center.
            if (e?.response?.status === 404) {
                convStore.clear(myCard);
                try { await startThread(); return; } catch { /* fall through */ }
            }
            toast.error(t('vehicleExp.err.generic', 'Something went wrong'));
        }
    };

    // Resume an existing conversation for this card.
    const existingConv = convStore.get(myCard);
    // The hint belongs on a LIVE thread only: it appears once a salesperson
    // has actually replied, never on an empty chat screen.
    const chatHasSalesReply = !!(chat?.messages || []).some((m) => m.sender_role && m.sender_role !== 'customer');

    const actionTitle = action === 'testdrive' ? t('vehicleExp.testDrive', 'Request a Test Drive')
        : t('vehicleExp.saveTicket', 'Save vehicle & ticket');

    return (
        <div className={`min-h-screen relative ${th.page}`} data-testid="vehicle-experience-page">
            {/* Shared "Change language" affordance — hides itself when the card
                has a single enabled language. */}
            <ChangeLanguageLink enabled={enabledLangs} onOpen={resetLang} variant="dark" />
            {/* The way back to the cars already looked at. Renders itself only
                from the second vehicle of the visit onwards. */}
            <MyVisitTray organizationId={data.organization_id} currentCardId={myCard}
                key={visitCount}
                onJump={(target) => navigate(`/vehicle/${target}`)} />
            <VehicleShowroom
                vehicle={v}
                dealership={data.dealership}
                dealerLogo={data.dealer_logo}
                salesStatus={data.sales_status}
                inCompare={inCompare}
                theme={data.buyer_theme}
                paymentEstimate={!!data.payment_estimate}
                onCarfaxView={() => vehiclePublicAPI
                    .activity(cardId, 'carfax_viewed', sessionId)
                    .catch(() => { })}
                onFinance={(data.fi?.financing || data.fi?.warranty || data.fi?.insurance)
                    ? () => navigate(`/vehicle/${cardId}/finance`) : undefined}
                onTradeIn={data.fi?.trade_in ? () => navigate(`/vehicle/${cardId}/trade-in`) : undefined}
                onDetails={() => navigate(`/vehicle/${cardId}/details`)}
                onImages={() => navigate(`/vehicle/${cardId}/details?tab=images`)}
                /* The engine (authorized special_instances) is the source of
                   truth; the legacy special_offer_* fields only supply a link
                   for a one-off promo with no schedule behind it. */
                promotionOn={!!(special && specialText) || !!v.special_offer_enabled}
                promotionUrl={v.special_offer_url || null}
                onPromotion={() => {
                    setSpecialOpen(true);
                    // The promotion badge IS the special-offer click. Nothing
                    // emitted 'click' before, so "Opened the special offer"
                    // could never appear in Buyer Activity even though the
                    // panel and the allow-list were ready for it.
                    vehiclePublicAPI.offerEvent(cardId, 'click', sessionId).catch(() => {});
                }}
                /* NO back button here on purpose: this page is the ENTRY POINT of
                   the buyer experience (tap / QR / SMS link), so "back" had
                   nowhere safe to go — history.length > 1 sent them to whatever
                   preceded the tag, and the empty-history fallback dropped them
                   on the YulOnTap portal. The nested screens (F&I, Trade-In,
                   Compare) keep their own back arrows, which navigate explicitly
                   to /vehicle/:cardId and are unaffected. */
                onContact={() => { setChatLead({}); setChatInput(''); setChat({ reqId: existingConv || null, messages: [], salesStatus: data.sales_status }); }}
                onTestDrive={() => openAction('testdrive')}
                onSave={() => openAction('save')}
                onCompare={toggleCompare}
                onShare={openShareFlow}
                onOfferDeposit={openOfferDeposit}
            />

            {/* PHASE F — the AUTHORIZED promotion's detail modal, opened from the
                gentle badge on the hero (which is the only promotion surface). */}
            {special && specialText && (
                <>
                    <Dialog open={specialOpen} onOpenChange={setSpecialOpen}>
                        <DialogContent className="sm:max-w-sm bg-neutral-900 text-neutral-100 border-emerald-500/40" data-testid="ve-eligible-special-modal">
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2 text-emerald-400">
                                    <BadgePercent className="w-5 h-5" />{t('vehicleExp.special.title', 'Your Eligible Promotion')}
                                </DialogTitle>
                                <DialogDescription className="text-xs text-neutral-400" data-testid="ve-special-context">{title} · {t('usedVehicles.stockShort', 'Stock')} #{v.stock_number}</DialogDescription>
                            </DialogHeader>
                            <div className="py-4 text-center">
                                <p className="text-3xl font-extrabold text-white leading-tight" data-testid="ve-special-incentive">{specialText}</p>
                                <p className="text-sm text-neutral-400 mt-3">{t('vehicleExp.special.footnote', 'Mention this offer to our team.')}</p>
                            </div>
                            <Button className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold" onClick={() => setSpecialOpen(false)} data-testid="ve-special-close">{t('vehicleExp.gotIt', 'Got it')}</Button>
                        </DialogContent>
                    </Dialog>
                </>
            )}

            {/* Floating "View comparison" bar */}
            {cmpCount > 0 && (
                <div className="fixed left-0 right-0 z-40 flex justify-center px-4" style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 152px)' }}>
                    <button
                        onClick={() => navigate('/vehicle/compare')}
                        className="max-w-md w-full flex items-center justify-between gap-3 rounded-full bg-orange-500 text-black font-bold px-5 py-3 shadow-2xl shadow-orange-900/40 hover:bg-orange-400 transition-colors"
                        data-testid="ve-view-compare"
                    >
                        <span className="flex items-center gap-2"><Columns2 className="w-5 h-5" />{t('vehicleExp.viewComparison', 'View comparison')}</span>
                        <span className="bg-black/20 rounded-full px-2.5 py-0.5 text-sm" data-testid="ve-compare-count">{cmpCount}</span>
                    </button>
                </div>
            )}

            {/* First-time compare explainer */}
            <Dialog open={introOpen} onOpenChange={setIntroOpen}>
                <DialogContent className="sm:max-w-sm bg-neutral-900 text-neutral-100 border-neutral-800" data-testid="ve-compare-intro">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Columns2 className="w-5 h-5 text-orange-400" />{t('vehicleExp.compareIntroTitle', 'Compare Vehicles')}</DialogTitle>
                        <DialogDescription className="text-neutral-400 text-sm leading-relaxed pt-1">
                            {t('vehicleExp.compareIntroBody', "Compare this vehicle against others you're considering. Scan other vehicles on the lot and add them — we'll show them side-by-side so you can see how they differ.")}
                        </DialogDescription>
                    </DialogHeader>
                    <Button className="w-full bg-orange-500 hover:bg-orange-400 text-black font-semibold" onClick={() => setIntroOpen(false)} data-testid="ve-compare-intro-ok">{t('vehicleExp.gotIt', 'Got it')}</Button>
                </DialogContent>
            </Dialog>

            {/* Action sheet: test drive / save / (initial) contact */}
            <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
                <DialogContent className="sm:max-w-sm bg-neutral-900 text-neutral-100 border-neutral-800" data-testid="ve-action-modal">
                    <DialogHeader>
                        <DialogTitle>{actionTitle}</DialogTitle>
                        <DialogDescription className="text-xs text-neutral-400" data-testid="ve-action-context">{title} · {t('usedVehicles.stockShort', 'Stock')} #{v.stock_number} · {data.dealership}</DialogDescription>
                    </DialogHeader>

                    {done ? (
                        <div className="py-6 flex flex-col items-center text-center gap-3" data-testid="ve-action-done">
                            <CheckCircle2 className="w-12 h-12 text-emerald-500" />
                            <p className="font-medium text-neutral-100">
                                {action === 'testdrive' ? t('vehicleExp.doneTestdrive', 'Request sent — the dealership will confirm your time shortly.')
                                    : t('vehicleExp.saveTicketDone', 'Saved. Check your email.')}
                            </p>
                            {action === 'save' && ticket && (
                                <div className="w-full rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3"
                                    data-testid="ve-save-ticket">
                                    <p className="text-[11px] uppercase tracking-wide text-emerald-300">
                                        {t('vehicleExp.saveTicketNumber', 'Your ticket number')}
                                    </p>
                                    <p className="mt-0.5 text-xl font-bold tracking-[0.12em] text-white"
                                        data-testid="ve-save-ticket-code">{ticket}</p>
                                </div>
                            )}
                            <Button variant="outline" className="border-neutral-700 bg-transparent" onClick={() => setAction(null)} data-testid="ve-action-close">{t('common.close', 'Close')}</Button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {action === 'testdrive' && data.test_drive_gift && !tdIntent && (
                                <div className="space-y-3" data-testid="ve-td-choice">
                                    <p className="text-sm text-neutral-300">{t('gift.choiceTitle', 'Two ways to do this — pick either one.')}</p>
                                    <div className="grid grid-cols-2 gap-2.5">
                                        <button type="button" onClick={() => setTdIntent('book')} data-testid="ve-td-choice-book" data-tactile="select"
                                            className="min-h-[104px] rounded-2xl border border-emerald-400/50 bg-emerald-500/15 px-3 py-3 text-left transition-transform active:scale-[0.98] motion-reduce:active:scale-100">
                                            <CalendarClock className="w-5 h-5 text-emerald-300 mb-1.5" />
                                            <span className="block text-sm font-bold text-white leading-tight">{t('gift.choiceBook', 'Book Test Drive')}</span>
                                            <span className="block text-[11px] text-emerald-100/80 mt-1 leading-snug">{t('gift.choiceBookSub', 'Pick a day and time.')}</span>
                                        </button>
                                        <button type="button" onClick={() => setTdIntent('gift')} data-testid="ve-td-choice-gift" data-tactile="select"
                                            className="min-h-[104px] rounded-2xl border border-amber-400/60 bg-amber-500/15 px-3 py-3 text-left transition-transform active:scale-[0.98] motion-reduce:active:scale-100">
                                            <Gift className="w-5 h-5 text-amber-300 mb-1.5" />
                                            <span className="block text-sm font-bold text-white leading-tight">{t('gift.choiceGift', 'Receive a Gift from Us')}</span>
                                            <span className="block text-[11px] text-amber-100/80 mt-1 leading-snug">{t('gift.choiceGiftSub', '{{gift}} after your drive.', { gift: data.test_drive_gift_title || t('gift.genericGift', 'thank-you gift') })}</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                            {action === 'testdrive' && (!data.test_drive_gift || tdIntent) && (
                                <>
                                    {/* 9B — two ways in, ONE booking: the gift only
                                        exists after a completed drive, so both paths
                                        book the same drive. Never a review ask. */}
                                    {data.test_drive_gift && (
                                        <div className="flex items-start gap-2.5 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2.5" data-testid="ve-td-gift-note">
                                            <Gift className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                                            <p className="text-xs leading-relaxed text-amber-100">
                                                {tdIntent === 'gift'
                                                    ? t('gift.buyerGiftPath', 'Your {{gift}} is sent by {{dealership}} right after the drive — book a time and it is yours.', { dealership: data.dealership, gift: data.test_drive_gift_title || t('gift.genericGift', 'thank-you gift') })
                                                    : t('gift.buyerNote', 'Take the drive and {{dealership}} will send you a {{gift}} afterwards — our thanks for your time.', { dealership: data.dealership, gift: data.test_drive_gift_title || t('gift.genericGift', 'thank-you gift') })}
                                            </p>
                                        </div>
                                    )}
                                    <div className="grid grid-cols-2 gap-2">
                                        <Input className="bg-neutral-800 border-neutral-600 text-neutral-100 placeholder:text-neutral-400" placeholder={t('vehicleExp.fFirst', 'First name')} value={f.first_name || ''} onChange={(e) => upd('first_name', e.target.value)} data-testid="ve-td-first" />
                                        <Input className="bg-neutral-800 border-neutral-600 text-neutral-100 placeholder:text-neutral-400" placeholder={t('vehicleExp.fLast', 'Last name')} value={f.last_name || ''} onChange={(e) => upd('last_name', e.target.value)} data-testid="ve-td-last" />
                                    </div>
                                    <Input className="bg-neutral-800 border-neutral-600 text-neutral-100 placeholder:text-neutral-400" placeholder={t('vehicleExp.fEmailReq', 'Email')} value={f.email || ''} onChange={(e) => upd('email', e.target.value)} data-testid="ve-td-email" />
                                    <Input className="bg-neutral-800 border-neutral-600 text-neutral-100 placeholder:text-neutral-400" placeholder={t('vehicleExp.fPhone', 'Phone')} value={f.phone || ''} onChange={(e) => upd('phone', e.target.value)} data-testid="ve-td-phone" />
                                    <div className="grid grid-cols-2 gap-2">
                                        <Input className="bg-neutral-800 border-neutral-600 text-neutral-100 placeholder:text-neutral-400" type="date" value={f.date || ''} onChange={(e) => upd('date', e.target.value)} data-testid="ve-td-date" />
                                        <Input className="bg-neutral-800 border-neutral-600 text-neutral-100 placeholder:text-neutral-400" type="time" value={f.time || ''} onChange={(e) => upd('time', e.target.value)} data-testid="ve-td-time" />
                                    </div>
                                    <Textarea className="bg-neutral-800 border-neutral-600 text-neutral-100 placeholder:text-neutral-400" placeholder={t('vehicleExp.fMessageOpt', 'Anything else? (optional)')} value={f.message || ''} onChange={(e) => upd('message', e.target.value)} data-testid="ve-td-message" />
                                </>
                            )}
                            {action === 'save' && (
                                <>
                                    <p className="text-sm text-neutral-400" data-testid="ve-save-hint">
                                        {t('vehicleExp.saveTicketHint')}
                                    </p>
                                    <Input className="bg-neutral-800 border-neutral-600 text-neutral-100 placeholder:text-neutral-400"
                                        placeholder={t('vehicleExp.saveTicketEmail', 'Your email')}
                                        value={f.email || ''} onChange={(e) => upd('email', e.target.value)}
                                        data-testid="ve-save-email" />
                                    <label className="flex items-start gap-2 text-sm text-neutral-400 cursor-pointer">
                                        <Checkbox checked={!!f.vehicle_consent} onCheckedChange={(c) => upd('vehicle_consent', c)} data-testid="ve-save-consent" className="mt-0.5" />
                                        <span data-testid="ve-consent-text">{consentText}</span>
                                    </label>
                                </>
                            )}
                            {!(action === 'testdrive' && data.test_drive_gift && !tdIntent) && (
                                <Button className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold" onClick={submit} disabled={submitting} data-testid="ve-action-submit">
                                    {submitting ? t('common.saving', 'Saving…') : t('vehicleExp.send', 'Send')}
                                </Button>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Make an Offer / Leave a Deposit */}
            <Dialog open={!!od} onOpenChange={(o) => !o && (setOd(null), setOdDone(null))}>
                <DialogContent className="sm:max-w-sm bg-neutral-900 text-neutral-100 border-indigo-500/40" data-testid="ve-od-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <BadgeDollarSign className="w-5 h-5 text-indigo-400" />
                            {od === 'deposit' ? t('offerDeposit.depositTitle', 'Leave a Deposit')
                                : od === 'offer' ? t('offerDeposit.offerTitle', 'Make an Offer')
                                    : t('offerDeposit.chooseTitle', 'Make an Offer or Leave a Deposit')}
                        </DialogTitle>
                        <DialogDescription className="text-xs text-neutral-400" data-testid="ve-od-context">
                            {title} · {t('usedVehicles.stockShort', 'Stock')} #{v.stock_number} · {data.dealership}
                        </DialogDescription>
                    </DialogHeader>

                    {odDone ? (
                        <div className="py-4 flex flex-col items-center text-center gap-3" data-testid="ve-od-done">
                            <CheckCircle2 className="w-12 h-12 text-emerald-500" />
                            <p className="font-medium text-neutral-100">
                                {odDone.kind === 'deposit'
                                    ? t('offerDeposit.doneDeposit', 'Your deposit request is with the sales manager.')
                                    : t('offerDeposit.doneOffer', "Your offer is with the sales manager. You'll hear back right here.")}
                            </p>
                            {odDone.kind === 'deposit' && (
                                <div className="w-full rounded-xl border border-indigo-500/40 bg-indigo-500/10 p-3 text-left" data-testid="ve-od-etransfer">
                                    {odDone.etransfer_email ? (
                                        <>
                                            <p className="text-[11px] uppercase tracking-wide text-indigo-300">{t('offerDeposit.etransferTitle', 'Send your e-transfer to')}</p>
                                            <p className="text-sm font-semibold text-white break-all mt-0.5" data-testid="ve-od-etransfer-email">{odDone.etransfer_email}</p>
                                            <p className="text-[11px] text-neutral-400 mt-1.5">{t('offerDeposit.etransferHint', 'The dealership confirms your hold once the transfer is received. No payment is taken on this page.')}</p>
                                        </>
                                    ) : (
                                        <p className="text-xs text-neutral-300">{t('offerDeposit.etransferMissing', 'The dealership will contact you with deposit instructions.')}</p>
                                    )}
                                </div>
                            )}
                            <div className="flex gap-2 w-full">
                                <Button variant="outline" className="flex-1 border-neutral-700 bg-transparent text-neutral-200 hover:text-white hover:bg-white/10" onClick={() => { setOd(null); setOdDone(null); }} data-testid="ve-od-close">{t('common.close', 'Close')}</Button>
                                <Button className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white" onClick={() => openConversation(odDone.request_id)} data-testid="ve-od-view-conversation">{t('offerDeposit.viewConversation', 'View conversation')}</Button>
                            </div>
                        </div>
                    ) : od === 'choose' ? (
                        <div className="space-y-2.5" data-testid="ve-od-choose">
                            <p className="text-xs text-neutral-400">{t('offerDeposit.chooseHint', 'Both go straight to the sales manager. No account needed.')}</p>
                            <ActionRow color="#818CF8" icon={BadgeDollarSign} testid="ve-od-pick-offer"
                                label={t('offerDeposit.makeOffer', 'Make an Offer')}
                                sublabel={t('offerDeposit.makeOfferHint', 'Propose your price on this vehicle.')}
                                onClick={() => setOd('offer')} />
                            <ActionRow color="#818CF8" icon={HandCoins} testid="ve-od-pick-deposit"
                                label={t('offerDeposit.leaveDeposit', 'Leave a Deposit')}
                                sublabel={t('offerDeposit.leaveDepositHint', 'Ask the dealership to hold this vehicle for you.')}
                                onClick={() => setOd('deposit')} />
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="ve-od-form">
                            <Input className="bg-neutral-800 border-neutral-600 text-neutral-100 placeholder:text-neutral-400" placeholder={t('offerDeposit.fName', 'Your name (optional)')} value={odForm.name || ''} onChange={(e) => odUpd('name', e.target.value)} data-testid="ve-od-name" />
                            <p className="text-[11px] text-neutral-400">{t('offerDeposit.contactRule', 'Enter an email or a phone number so we can get back to you.')}</p>
                            <Input className="bg-neutral-800 border-neutral-600 text-neutral-100 placeholder:text-neutral-400" placeholder={t('vehicleExp.fEmailReq', 'Email')} value={odForm.email || ''} onChange={(e) => odUpd('email', e.target.value)} data-testid="ve-od-email" />
                            <Input className="bg-neutral-800 border-neutral-600 text-neutral-100 placeholder:text-neutral-400" placeholder={t('vehicleExp.fPhone', 'Phone')} value={odForm.phone || ''} onChange={(e) => odUpd('phone', e.target.value)} data-testid="ve-od-phone" />
                            <CurrencyInput className="bg-neutral-800 border-neutral-600 text-neutral-100 placeholder:text-neutral-400"
                                placeholder={od === 'deposit' ? t('offerDeposit.fDepositAmount', 'Deposit amount (e.g. 500)') : t('offerDeposit.fOfferAmount', 'Your offer (e.g. 48000)')}
                                value={odForm.amount || ''} onChange={(v) => odUpd('amount', v)} data-testid="ve-od-amount" />
                            {od === 'deposit' && (
                                <div className="flex flex-wrap gap-1.5" data-testid="ve-od-amount-presets">
                                    {[250, 500, 1000].map((a) => (
                                        <button key={a} type="button" onClick={() => odUpd('amount', String(a))}
                                            data-testid={`ve-od-amount-${a}`}
                                            className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${String(odForm.amount) === String(a) ? 'border-indigo-400 bg-indigo-500/25 text-white' : 'border-white/15 bg-white/[0.04] text-neutral-300 hover:bg-white/[0.08]'}`}>
                                            ${a}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {od === 'deposit' && (
                                <div>
                                    <p className="text-[11px] text-neutral-400 mb-1">{t('offerDeposit.fHoldUntil', 'Hold this vehicle until')}</p>
                                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="ve-od-hold-presets">
                                        {[['hold24h', 24, 'h'], ['hold48h', 48, 'h'], ['hold72h', 72, 'h'],
                                        ['hold4d', 4, 'd'], ['hold5d', 5, 'd'], ['hold7d', 7, 'd']].map(([key, n, unit]) => (
                                            <button key={key} type="button" data-testid={`ve-od-${key}`}
                                                onClick={() => {
                                                    const ms = unit === 'h' ? n * 3600e3 : n * 86400e3;
                                                    const d = new Date(Date.now() + ms);
                                                    odUpd('hold_until', d.toISOString().slice(0, 10));
                                                }}
                                                className="rounded-full px-3 py-1.5 text-xs font-semibold border border-white/15 bg-white/[0.04] text-neutral-300 hover:bg-white/[0.08] transition-colors">
                                                {t(`offerDeposit.${key}`, `${n} ${unit === 'h' ? 'hours' : 'days'}`)}
                                            </button>
                                        ))}
                                    </div>
                                    <Input type="date" className="bg-neutral-800 border-neutral-600 text-neutral-100" value={odForm.hold_until || ''} onChange={(e) => odUpd('hold_until', e.target.value)} data-testid="ve-od-hold" />
                                </div>
                            )}
                            <Textarea className="bg-neutral-800 border-neutral-600 text-neutral-100 placeholder:text-neutral-400" placeholder={t('offerDeposit.fNote', 'Anything we should know? (optional)')} value={odForm.note || ''} onChange={(e) => odUpd('note', e.target.value)} data-testid="ve-od-note" />
                            <Button className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-semibold" onClick={submitOfferDeposit} disabled={odBusy} data-testid="ve-od-submit">
                                {odBusy ? t('common.saving', 'Saving…')
                                    : od === 'deposit' ? t('offerDeposit.submitDeposit', 'Send my deposit request')
                                        : t('offerDeposit.submitOffer', 'Send my offer')}
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Two-way async conversation — SMS-style, opens directly (no pre-form) */}
            <Dialog open={!!chat} onOpenChange={(o) => !o && closeChat()}>
                <DialogContent className="sm:max-w-sm bg-neutral-900 text-neutral-100 border-neutral-800 p-0 gap-0 top-2 translate-y-0 max-h-[calc(100dvh-5.5rem)] flex flex-col overflow-y-auto" data-testid="ve-chat">
                    <DialogHeader className="p-4 border-b border-neutral-800 bg-neutral-950/60 text-left">
                        <DialogTitle className="text-base flex items-center gap-2.5">
                            <span className="w-9 h-9 rounded-full bg-blue-600/20 border border-blue-500/40 flex items-center justify-center flex-shrink-0"><MessageCircle className="w-[18px] h-[18px] text-blue-300" /></span>
                            <span className="truncate">{data.dealership}</span>
                        </DialogTitle>
                        <DialogDescription className="text-xs text-neutral-400 truncate pl-[46px] -mt-1" data-testid="ve-chat-context">
                            {title} · {t('usedVehicles.stockShort', 'Stock')} #{v.stock_number}
                        </DialogDescription>
                    </DialogHeader>

                    {/* Optional inline lead — NEVER blocks the chat. Customers can
                        send questions with no name/email/phone; adding contact just
                        lets Sales follow up outside the live thread. */}
                    {!chat?.reqId && (
                        <div className="px-4 pt-3 pb-2.5 space-y-2 border-b border-neutral-800/70 bg-white/[0.02]" data-testid="ve-chat-lead">
                            <p className="text-[11px] text-neutral-300">{t('vehicleExp.chatLeadHintOptional', 'Just start typing — no email or phone needed. Optionally add contact info if you\'d like Sales to reach you later.')}</p>
                            <div className="grid grid-cols-2 gap-2">
                                <Input placeholder={t('vehicleExp.fNameOptional', 'Your name (optional)')} value={chatLead.name || ''} onChange={(e) => setChatLead((p) => ({ ...p, name: e.target.value }))} className="h-9 bg-neutral-800 border-neutral-600 text-sm text-neutral-100 placeholder:text-neutral-400" data-testid="ve-chat-lead-name" />
                                <Input placeholder={t('vehicleExp.fEmailOrPhoneOptional', 'Email or phone (optional)')} value={chatLead.contact || ''} onChange={(e) => setChatLead((p) => ({ ...p, contact: e.target.value }))} className="h-9 bg-neutral-800 border-neutral-600 text-sm text-neutral-100 placeholder:text-neutral-400" data-testid="ve-chat-lead-contact" />
                            </div>
                        </div>
                    )}

                    {/* Offer / Deposit state — including a dealer counter the buyer
                        can accept or decline right here, with no account. */}
                    {chat?.negotiation && (() => {
                        const n = chat.negotiation;
                        const isDep = n.request_type === 'vehicle_deposit';
                        const mine = isDep ? n.deposit_amount : n.offer_amount;
                        const fmtMoney = (a) => (a == null || a === '' ? '' : fmtCurrency(a));
                        return (
                            <div className="px-4 py-3 border-b border-neutral-800/70 bg-indigo-500/[0.07] space-y-2" data-testid="ve-chat-negotiation">
                                <p className="text-[11px] uppercase tracking-wide text-indigo-300">
                                    {isDep ? t('offerDeposit.yourDeposit', 'Your deposit') : t('offerDeposit.yourOffer', 'Your offer')}
                                    : <span className="text-white font-semibold" data-testid="ve-neg-amount">{fmtMoney(mine)}</span>
                                    {isDep && n.requested_hold_until ? ` · ${n.requested_hold_until}` : ''}
                                </p>
                                {n.manager_decision === 'accepted' && <p className="text-sm text-emerald-400 font-medium" data-testid="ve-neg-accepted">{t('offerDeposit.acceptedByDealer', 'Accepted by the dealership')}</p>}
                                {n.manager_decision === 'refused' && <p className="text-sm text-rose-300 font-medium" data-testid="ve-neg-refused">{t('offerDeposit.refusedByDealer', 'Declined by the dealership')}</p>}
                                {n.manager_decision === 'countered' && (
                                    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5" data-testid="ve-neg-counter">
                                        <p className="text-xs text-amber-200 font-semibold">{t('offerDeposit.counterTitle', 'The dealership countered')}</p>
                                        <p className="text-sm text-white mt-0.5" data-testid="ve-neg-counter-terms">
                                            {n.counter_amount ? fmtMoney(n.counter_amount) : ''}
                                            {n.counter_hold_until ? `${n.counter_amount ? ' · ' : ''}${t('offerDeposit.counterHold', 'Hold until')} ${n.counter_hold_until}` : ''}
                                        </p>
                                        {n.manager_note && <p className="text-xs text-neutral-300 mt-1 italic">{n.manager_note}</p>}
                                        {n.counter_pending ? (
                                            <div className="flex gap-2 mt-2.5">
                                                <Button size="sm" className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold" disabled={counterBusy} onClick={() => respondToCounter('accept')} data-testid="ve-neg-accept">
                                                    {t('offerDeposit.counterAccept', 'Accept')}
                                                </Button>
                                                <Button size="sm" variant="outline" className="flex-1 border-neutral-600 bg-transparent text-neutral-200" disabled={counterBusy} onClick={() => respondToCounter('decline')} data-testid="ve-neg-decline">
                                                    {t('offerDeposit.counterDecline', 'Decline')}
                                                </Button>
                                            </div>
                                        ) : (
                                            <p className="text-xs mt-1.5 text-neutral-300" data-testid="ve-neg-buyer-decision">
                                                {n.buyer_decision === 'accepted' ? t('offerDeposit.counterAccepted', 'You accepted this counter.')
                                                    : n.buyer_decision === 'declined' ? t('offerDeposit.counterDeclined', 'You declined this counter.') : ''}
                                            </p>
                                        )}
                                    </div>
                                )}
                                {isDep && n.etransfer_email && (
                                    <p className="text-[11px] text-neutral-400" data-testid="ve-neg-etransfer">
                                        {t('offerDeposit.etransferTitle', 'Send your e-transfer to')}: <span className="text-neutral-200 break-all">{n.etransfer_email}</span>
                                    </p>
                                )}
                            </div>
                        );
                    })()}

                    {/* Shown only once Sales has actually replied. */}
                    {chatHasSalesReply && (
                        <button type="button" data-testid="ve-chat-save-hint"
                            onClick={() => { setChat(null); openAction('save'); }}
                            className="mx-4 mb-1 mt-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-left text-[12px] leading-snug text-emerald-200">
                            {t('vehicleExp.chatSaveHint')}
                        </button>
                    )}

                    {/* Same thread component the confidential insurance chat uses. */}
                    <BuyerChatThread
                        messages={chat?.messages || []}
                        input={chatInput}
                        onInputChange={setChatInput}
                        onSend={sendChat}
                        peerLabel={t('vehicleExp.sales', 'Sales')}
                        empty={t('vehicleExp.chatEmpty', 'Your conversation with Sales will appear here.')}
                        note={{
                            placeholder: t('vehicleExp.typeMessage', 'Type a message…'),
                            text: t('vehicleExp.replyNote', 'To be notified when we reply, leave an email or a mobile number. Otherwise keep this page or its link handy and check back here.'),
                        }}
                    />
                </DialogContent>
            </Dialog>

            {/* Send to someone — trackable vehicle share */}
            <Dialog open={share} onOpenChange={(o) => !o && setShare(false)}>
                <DialogContent className="sm:max-w-sm bg-neutral-900 text-neutral-100 border-neutral-800" data-testid="ve-share-modal">
                    <DialogHeader>
                        <DialogTitle>{t('vehicleShare.sendToSomeone', 'Send to someone')}</DialogTitle>
                        <DialogDescription className="text-xs text-neutral-400" data-testid="ve-share-context">
                            {t('vehicleShare.supporting', 'Share this vehicle with someone you know.')} · {title}
                        </DialogDescription>
                    </DialogHeader>
                    {shareSent ? (
                        <div className="py-5 flex flex-col items-center text-center gap-3" data-testid="ve-share-done">
                            <CheckCircle2 className="w-12 h-12 text-emerald-500" />
                            <p className="font-medium text-neutral-100">{t('vehicleShare.sent', 'Your vehicle has been shared.')}</p>
                            <div className="w-full flex items-center gap-2">
                                <Input readOnly value={shareUrl} className="text-xs bg-neutral-800 border-neutral-700" data-testid="ve-share-url" />
                                <Button variant="outline" className="border-neutral-700 bg-transparent shrink-0" onClick={copyShareLink} data-testid="ve-share-copy">
                                    <Copy className="w-4 h-4 mr-1.5" />{t('vehicleShare.copyLink', 'Copy link')}
                                </Button>
                            </div>
                            <Button variant="outline" className="border-neutral-700 bg-transparent" onClick={() => setShare(false)} data-testid="ve-share-close">{t('common.close', 'Close')}</Button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <Input placeholder={t('vehicleShare.recipientName', "Recipient's name (optional)")} value={shareForm.name || ''} onChange={(e) => setShareForm((p) => ({ ...p, name: e.target.value }))} data-testid="ve-share-name" />
                            <Input placeholder={t('vehicleShare.recipientEmail', 'Recipient email (optional)')} value={shareForm.email || ''} onChange={(e) => setShareForm((p) => ({ ...p, email: e.target.value }))} data-testid="ve-share-email" />
                            <Input placeholder={t('vehicleShare.recipientPhone', 'Recipient phone (optional)')} value={shareForm.phone || ''} onChange={(e) => setShareForm((p) => ({ ...p, phone: e.target.value }))} data-testid="ve-share-phone" />
                            <Textarea placeholder={t('vehicleShare.personalMessage', 'Add a short message (optional)')} value={shareForm.message || ''} onChange={(e) => setShareForm((p) => ({ ...p, message: e.target.value }))} data-testid="ve-share-message" />
                            <div className="flex gap-2">
                                <Button variant="outline" className="flex-1 border-neutral-700 bg-transparent" onClick={() => setShare(false)} data-testid="ve-share-cancel">{t('common.cancel', 'Cancel')}</Button>
                                <Button className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-semibold" onClick={submitShare} disabled={shareBusy} data-testid="ve-share-submit">
                                    {shareBusy ? t('common.saving', 'Saving…') : (
                                        <><Share2 className="w-4 h-4 mr-1.5" />{t('vehicleShare.share', 'Share')}</>
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
